import Button from "../../../components/common/Buttons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useTargetData } from "../hooks/useTargetData";
import { ProductInterface } from "../../../interfaces/productInterface";
import { findItem } from "../utils/findItem";
import { getPromotionalPrice } from "../utils/getPromotionalPrice";
import { CategoryInterface } from "../../../interfaces/categoryInterface";
import formatToMoney from "../../../utils/formatToMoney";

type PromotionTargetsTableProps = {
  discountTarget: "product-category" | "product";
  targetItemsId: string[];
  discountPercentage: number;
  onEdit?: (itemId: string) => void;
  onDelete?: (itemId: string) => void;
};

type PromotionCategoryTableProps = Pick<
  PromotionTargetsTableProps,
  "targetItemsId" | "onEdit" | "onDelete"
> & {
  categories: CategoryInterface[];
};

type PromotionProductTableProps = Pick<
  PromotionTargetsTableProps,
  "targetItemsId" | "discountPercentage" | "onEdit" | "onDelete"
> & {
  products: ProductInterface[];
  isEditable?: boolean;
  shouldDisplayProductsByList?: boolean;
};

type ProductItemRowProps = Pick<
  PromotionTargetsTableProps,
  "discountPercentage" | "onEdit" | "onDelete"
> & {
  item: ProductInterface;
  isEditable?: boolean;
  shouldDisplayProductsByList?: boolean;
};

const classes = {
  tableContainer: "border rounded-lg overflow-hidden mt-2 overflow-x-auto",
  table: "min-w-full",
  tableHeader: "bg-gray-100",
  tableCell: "py-2 px-4 border-b",
  tableCellMoney: "py-2 px-4 border-b text-right",
  optionsCell: "flex gap-2 items-end justify-start",
};

export const PromotionCategoryTable = (props: PromotionCategoryTableProps) => {
  const { targetItemsId, onEdit, onDelete, categories } = props;
  return (
    <div className={classes.tableContainer}>
      <table className={classes.table}>
        <thead className={classes.tableHeader}>
          <tr>
            <th className={classes.tableCell}>Categoría</th>
            <th className={classes.tableCell}>Opciones</th>
          </tr>
        </thead>
        <tbody>
          {targetItemsId.map((target) => {
            const item = findItem("category", categories, target);
            if (!item) return null;
            return (
              <tr key={`row-category-target-${target}`}>
                <td className={classes.tableCell}>{item.name}</td>
                <td className={classes.tableCell}>
                  <div className={classes.optionsCell}>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => onEdit?.(item._id ?? "")}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => onDelete?.(item._id ?? "")}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ProductItemRow = (props: ProductItemRowProps) => {
  const { item, discountPercentage, onEdit, onDelete, isEditable } = props;
  const promotionalPrice = getPromotionalPrice(item.price, discountPercentage);
  return (
    <tr>
      <td className={classes.tableCell}>{item.name}</td>
      <td className={classes.tableCellMoney}>{formatToMoney(item.price)}</td>
      <td className={classes.tableCellMoney}>
        {formatToMoney(promotionalPrice)}
      </td>
      {isEditable && (
        <td className={classes.tableCell}>
          <div className={classes.optionsCell}>
            <Button
              size="small"
              color="primary"
              onClick={() => onEdit?.(item._id ?? "")}
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            <Button
              size="small"
              color="secondary"
              onClick={() => onDelete?.(item._id ?? "")}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
};

export const PromotionProductTable = (props: PromotionProductTableProps) => {
  const {
    targetItemsId,
    discountPercentage,
    onEdit,
    onDelete,
    products,
    isEditable = true,
    shouldDisplayProductsByList = false,
  } = props;

  return (
    <div className={classes.tableContainer}>
      <table className={classes.table}>
        <thead className={classes.tableHeader}>
          <tr>
            <th className={classes.tableCell}>Productos</th>
            <th className={classes.tableCell}>Precio sin promoción</th>
            <th className={classes.tableCell}>Precio con promoción</th>
            {isEditable && <th className={classes.tableCell}>Opciones</th>}
          </tr>
        </thead>
        <tbody>
          {!shouldDisplayProductsByList &&
            targetItemsId.map((target) => {
              const item = findItem("product", products, target);
              if (!item) return null;
              return (
                <ProductItemRow
                  key={`row-product-${item._id}`}
                  item={item}
                  discountPercentage={discountPercentage}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  isEditable={isEditable}
                />
              );
            })}
          {shouldDisplayProductsByList &&
            products.map((item) => (
              <ProductItemRow
                key={`row-product-${item._id}`}
                item={item}
                discountPercentage={discountPercentage}
                onEdit={onEdit}
                onDelete={onDelete}
                isEditable={isEditable}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
};

export const PromotionTargetsTable = (props: PromotionTargetsTableProps) => {
  const { discountTarget, targetItemsId, discountPercentage, onEdit, onDelete } =
    props;

  const { targetItems, isLoading } = useTargetData(discountTarget);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (discountTarget === "product-category") {
    return (
      <PromotionCategoryTable
        targetItemsId={targetItemsId}
        onEdit={onEdit}
        onDelete={onDelete}
        categories={targetItems as CategoryInterface[]}
      />
    );
  }

  return (
    <PromotionProductTable
      targetItemsId={targetItemsId}
      discountPercentage={discountPercentage}
      onEdit={onEdit}
      onDelete={onDelete}
      products={targetItems as ProductInterface[]}
    />
  );
};
